import { FilterTypes, SearchParams } from '@/constants/common';
import { FilterFormValues, SearchParams as SearchParamsType } from '@/types/common';
import { buildUrlWithParams } from '@/utils/helpers';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { usePath } from '@/hooks/usePath';
import { useAuth } from '@/context/AuthContext';
import { CheckboxOptionType } from 'antd/lib/checkbox/Group';
import { FormInstance } from 'antd/lib';
import { checkIsFormValuesChanged } from '@/utils/forms';
import { EventsEquippingLevels } from '@/constants/events';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearch } from '@/hooks/useSearch';

export const useFilter = (
  form: FormInstance,
  filtersOptionsAreLoading: boolean,
  filterType: FilterTypes,
  activityLanguages: CheckboxOptionType[],
  activityMaterials: CheckboxOptionType[] = [],
  equippingTypeValue = '',
) => {
  const router = useRouter();
  const pathname = usePath();
  const searchParams = useSearchParams();
  const { meDataLoading, preferredLanguageId } = useAuth();
  const { isAuthenticated } = useAuth0();
  const { searchUrlParam } = useSearch(filterType);

  const [filtersCleared, setFiltersCleared] = useState<boolean>();
  const [formValuesChanged, setFormValuesChanged] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState<FilterFormValues>({});
  const [languageParamCanBeAdded, setLanguageParamCanBeAdded] = useState(true);

  const activityType = searchParams.get(SearchParams.filterActivityType);
  const equippingLevel = searchParams.get(SearchParams.filterEquippingLevel);

  const preferredLanguageIdParam =
    filterType === FilterTypes.materials
      ? searchParams.get(SearchParams.filterMaterialFileContentLanguage)
      : searchParams.get(SearchParams.filterLanguage);

  const preferredLanguagePresented = activityLanguages.some((item) => item.value.toString() === preferredLanguageId);

  const languageSearchParamWillBeAdded =
    isAuthenticated &&
    !searchParams.size &&
    languageParamCanBeAdded &&
    (((meDataLoading || filtersOptionsAreLoading) && !preferredLanguageIdParam) ||
      (!!preferredLanguageId && !preferredLanguageIdParam && preferredLanguagePresented));

  const activitySearchParamWillBeAdded =
    filterType === FilterTypes.common
      ? (activityType === equippingTypeValue && !equippingLevel) ||
        (!!equippingLevel &&
          (equippingLevel.includes(EventsEquippingLevels.initial) ||
            equippingLevel.includes(EventsEquippingLevels.advanced) ||
            equippingLevel.includes(EventsEquippingLevels.supplemental)) &&
          !activityType)
      : false;

  const searchParamsWillBeAdded = languageSearchParamWillBeAdded || activitySearchParamWillBeAdded;

  useEffect(() => {
    if (!languageSearchParamWillBeAdded || !preferredLanguageId || !preferredLanguagePresented) return;

    const filters: SearchParamsType = {};

    if (filterType === FilterTypes.materials) {
      filters[SearchParams.filterMaterialFileContentLanguage] = preferredLanguageId;
      form.setFieldValue(SearchParams.filterMaterialFileContentLanguage, [preferredLanguageId]);
    } else {
      filters[SearchParams.filterLanguage] = preferredLanguageId;
      form.setFieldValue(SearchParams.filterLanguage, [preferredLanguageId]);
    }

    router.push(buildUrlWithParams(pathname, '', filters));
  }, [languageSearchParamWillBeAdded, preferredLanguageId, preferredLanguagePresented]);

  useEffect(() => {
    if (searchParamsWillBeAdded || filtersOptionsAreLoading) return;

    const values: Record<string, string[]> = {};

    searchParams.forEach((value, key) => {
      if (key === SearchParams.filterEquippingLevel) return;

      if (key === SearchParams.filterActivityType) {
        const typeValues: string[] = [];

        value.split(',').forEach((type) => {
          if (type === equippingTypeValue && equippingLevel) {
            const equippingTypeValues: string[] = [];

            equippingLevel.split(',').forEach((level) => {
              typeValues.push(`${equippingTypeValue}_${level}`);
            });

            values[key] = equippingTypeValues;
          } else {
            typeValues.push(type);
          }
        });

        values[key] = typeValues;
      } else if (key === SearchParams.filterMaterialAge) {
        values[SearchParams.filterMaterials] = activityMaterials
          .filter((material) => material.label?.toString().toLowerCase().includes(value))
          .map((material) => material.value.toString());
      } else {
        values[key] = value.split(',');
      }
    });

    if (filtersCleared !== !searchParams.size) setFiltersCleared(!searchParams.size);

    if (searchParams.size) {
      form.resetFields();
      setInitialFormValues(values);
      form.setFieldsValue(values);
    } else if (Object.entries(form.getFieldsValue()).length) {
      form.resetFields();
      setInitialFormValues({});
    }
  }, [searchParams, filtersOptionsAreLoading]);

  const applyFiltersHandler = (values: Record<string, string[]>) => {
    setFormValuesChanged(false);
    if (languageParamCanBeAdded) setLanguageParamCanBeAdded(false);

    const currentParams = new URLSearchParams(searchParams.toString());
    const filterParams: SearchParamsType = {};

    currentParams.forEach((value, key) => {
      if (key.includes('filter') && key !== searchUrlParam) {
        currentParams.delete(key);
      }
    });

    Object.entries(values).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length) {
        if (key === SearchParams.filterActivityType) {
          const filterTypeValue: string[] = [];
          const filterEquippingLevelValue: string[] = [];

          value.forEach((type, i) => {
            if (type.includes('_')) {
              const typeValue = type.split('_')[0];

              if (!filterTypeValue.includes(typeValue)) {
                filterTypeValue.push(typeValue);
              }

              filterEquippingLevelValue.push(type.split('_')[1]);
            } else {
              filterTypeValue.push(type);
            }
          });

          filterParams[key] = filterTypeValue.join(',');
          if (filterEquippingLevelValue) {
            filterParams[SearchParams.filterEquippingLevel] = filterEquippingLevelValue.join(',');
          }
        } else {
          filterParams[key] = value.join(',');
        }
      }
    });

    router.push(buildUrlWithParams(pathname, '', { ...Object.fromEntries(currentParams.entries()), ...filterParams }));
  };

  const resetFiltersHandler = () => {
    setFiltersCleared(true);
    setLanguageParamCanBeAdded(false);
    form.resetFields();

    const currentParams = new URLSearchParams(searchParams.toString());

    for (const [key] of searchParams.entries()) {
      if (key.includes('filter') && key !== searchUrlParam) {
        currentParams.delete(key);
      }
    }

    router.push(buildUrlWithParams(pathname, '', Object.fromEntries(currentParams.entries())));
  };

  const formValuesChangeHandler = (changedValues: Partial<FilterFormValues>, currentValues: FilterFormValues) => {
    const isFormChanged = checkIsFormValuesChanged(initialFormValues, currentValues);
    setFormValuesChanged(isFormChanged);
    setFiltersCleared(Object.values(currentValues).every((value) => !value?.length));
  };

  return {
    filtersCleared: !!filtersCleared,
    searchParamsWillBeAdded,
    applyFiltersHandler,
    resetFiltersHandler,
    formValuesChanged,
    formValuesChangeHandler,
  };
};
